import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ClipboardList, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import PageHeader from "@/components/PageHeader";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ESPECIALIDADES } from "@/pages/Funcionarios";

const especialidadeLabel = (v) => ESPECIALIDADES.find((e) => e.value === v)?.label || v;

const OUTROS_ATENDIMENTOS = [
  { value: "fisioterapia", label: "Fisioterapia" },
  { value: "fonoaudiologia", label: "Fonoaudiologia" },
  { value: "psicoterapia", label: "Psicoterapia" },
  { value: "terapia_ocupacional", label: "Terapia Ocupacional" },
  { value: "hidroterapia", label: "Hidroterapia" },
];

const MATERIAIS = [
  { value: "sela", label: "Sela" },
  { value: "manta", label: "Manta" },
  { value: "manta_estribos", label: "Manta com estribos" },
];

const emptyFicha = {
  medicamentos_uso: "",
  ja_fez_equoterapia: null,
  outros_atendimentos: [],
  outros_atendimentos_desc: "",
  gravidez_planejada: null,
  fez_prenatal: null,
  tipo_parto: "",
  semanas_gestacao: "",
  pos_natal: "",
  complicacoes: "",
  marco_cabeca_meses: "",
  marco_sentar_meses: "",
  marco_engatinhar_meses: "",
  marco_andar_meses: "",
  // Diagnóstico detalhado
  diagnostico_descoberta: "",     // texto: quando descobriu?
  // Avaliação física
  tonus_muscular: null,
  // Órteses/Próteses
  usa_orteses: null,
  orteses_desc: "",
  // Decúbito matrix
  mudancas_decubito: {
    sentado_pe: "",
    pe_sentado: "",
    sentado_deitado: "",
    dorsal_ventral: "",
    ventral_dorsal: "",
  },
  linguagem_desenvolvimento: "",
  primeira_palavra_idade: "",         // texto livre "aos 12 meses"
  constroi_frases: null,              // Sim/Não
  ansiedade: 0,
  autoestima: 0,
  autoconfianca: 0,
  introversao: 0,
  interacao_social: "",
  interacao_familiar: "",
  habitos_sono: "",
  dorme_sozinho: null,
  sono_agitado: null,
  personalidade: "",           // extrovertido/introvertido/timido
  tem_amigos: null,
  amizade_facil: null,
  estuda: null,
  escola_nome: "",
  alimentacao: "",
  cardapio: "",
  atividades_vida_diaria: "",
  // Ambiente familiar e social
  tem_irmaos: null,
  irmaos_descricao: "",
  pais_casados: null,
  quem_acompanha_paciente: "",
  familiares_com_deficiencia_desc: "",
  convive_com_drogas_alcool_desc: "",
  // Rotina e comportamento
  amamentacao_desmame: "",
  range_dentes: null,
  fala_dormindo: null,
  comporta_faixa_etaria: null,
  e_agressivo: null,
  e_teimoso: null,
  e_carinhoso: null,
  tem_medos: "",
  tem_manias_tiques: "",
  // Interesses e convívio
  assiste_tv_musica: "",
  passeios_onde: "",
  comportamento_escolar: "",
  tem_interesse_sexual: null,
  // Origem
  como_conheceu: "",
  acompanhante_avaliacao: "",
  queixa_principal: "",
  expectativas_familia: "",
  objetivos_terapia: "",
  objetivo_geral: "",
  cavalo_id: "",
  terapeuta_id: "",
  material_utilizado: "",
  // Horário desejado
  dias_disponiveis: [],
  horario_preferido: "",
  disponibilidade_obs: "",
};

export default function FichaAvaliacao() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [praticante, setPraticante] = useState(null);
  const [form, setForm] = useState(emptyFicha);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cavalos, setCavalos] = useState([]);
  const [terapeutas, setTerapeutas] = useState([]);
  const [showFechar, setShowFechar] = useState(false);
  const [dataInicio, setDataInicio] = useState("");
  const [meses, setMeses] = useState(6);
  const [fechando, setFechando] = useState(false);
  // Dados clínicos do praticante (armazenados no doc do praticante, editados aqui)
  const [pratClinico, setPratClinico] = useState({
    altura_cm: "", dominancia: "", tipo_sanguineo: "", diagnostico: "", cid: "",
  });

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/praticantes/${id}`),
      api.get(`/praticantes/${id}/ficha-avaliacao`),
      api.get("/cavalos").catch(() => ({ data: [] })),
      api.get("/funcionarios", { params: { cargo: "terapeuta" } }).catch(() => ({ data: [] })),
    ]).then(([p, f, cav, ter]) => {
      if (!alive) return;
      setPraticante(p.data);
      setPratClinico({
        altura_cm: p.data.altura_cm ?? "",
        dominancia: p.data.dominancia || "",
        tipo_sanguineo: p.data.tipo_sanguineo || "",
        diagnostico: p.data.diagnostico || "",
        cid: p.data.cid || "",
      });
      setCavalos((cav.data || []).filter((c) => c.ativo !== false));
      setTerapeutas((ter.data || []).filter((t) => (t.situacao || "ativo") === "ativo"));
      if (f.data) {
        setForm({
          ...emptyFicha, ...f.data,
          semanas_gestacao: f.data.semanas_gestacao ?? "",
          marco_cabeca_meses: f.data.marco_cabeca_meses ?? "",
          marco_sentar_meses: f.data.marco_sentar_meses ?? "",
          marco_engatinhar_meses: f.data.marco_engatinhar_meses ?? "",
          marco_andar_meses: f.data.marco_andar_meses ?? "",
        });
      }
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["semanas_gestacao", "marco_cabeca_meses", "marco_sentar_meses", "marco_engatinhar_meses", "marco_andar_meses"].forEach((k) => {
        if (payload[k] === "") delete payload[k];
        else payload[k] = parseInt(payload[k], 10);
      });
      await api.put(`/praticantes/${id}/ficha-avaliacao`, payload);
      // Salva também os dados clínicos direto no praticante
      const pratPayload = {
        altura_cm: pratClinico.altura_cm === "" ? null : parseFloat(pratClinico.altura_cm),
        dominancia: pratClinico.dominancia || null,
        tipo_sanguineo: pratClinico.tipo_sanguineo || null,
        diagnostico: pratClinico.diagnostico || null,
        cid: pratClinico.cid || null,
      };
      await api.patch(`/praticantes/${id}`, pratPayload);
      toast.success("Ficha de avaliação salva");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleFecharAvaliacao = async () => {
    if (!dataInicio) { toast.error("Informe a data de início"); return; }
    if (!form.horario_preferido) { toast.error("Preencha o horário preferido antes"); return; }
    if (!(form.dias_disponiveis || []).length) { toast.error("Selecione pelo menos um dia da semana"); return; }
    if (!form.cavalo_id || !form.terapeuta_id) { toast.error("Selecione cavalo e terapeuta na ficha"); return; }
    setFechando(true);
    try {
      const dowMap = { seg: 0, ter: 1, qua: 2, qui: 3, sex: 4, sab: 5, dom: 6 };
      const horarios = (form.dias_disponiveis || []).map((d) => ({
        dia_semana: dowMap[d], hora: form.horario_preferido, duracao_min: 60,
      }));
      await handleSave();
      await api.patch(`/praticantes/${id}`, { tipo: "ativo" });
      await api.post("/agenda/recorrencia", {
        praticante_id: id, terapeuta_id: form.terapeuta_id, cavalo_id: form.cavalo_id,
        horarios, data_inicio: dataInicio, meses: parseInt(meses, 10), tipo: "sessao",
      });
      toast.success("Avaliação fechada! Sessões agendadas na agenda 🎉");
      setShowFechar(false);
      setTimeout(() => navigate("/agenda"), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao agendar sessões");
    } finally { setFechando(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/praticantes/${id}/ficha-avaliacao`);
      toast.success("Ficha excluída");
      setForm(emptyFicha);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir");
    }
  };

  if (loading) return <div className="p-10 text-center text-muted-foreground">Carregando…</div>;
  if (!praticante) return <div className="p-10 text-center text-muted-foreground">Praticante não encontrado</div>;

  const toggleAtendimento = (val) => {
    const cur = form.outros_atendimentos || [];
    setForm({
      ...form,
      outros_atendimentos: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val],
    });
  };

  return (
    <div data-testid="ficha-avaliacao-page" className="space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate("/praticantes")} data-testid="ficha-back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <PageHeader
          icon={ClipboardList}
          title={`Ficha de Avaliação — ${praticante.nome}`}
          description="Preenchida pelo terapeuta na consulta de avaliação. Não faz parte do cadastro básico."
          actions={
            <div className="flex gap-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-destructive border-destructive/40 hover:bg-destructive/10" data-testid="ficha-delete-btn">
                    <Trash2 className="h-4 w-4 mr-1" /> Excluir ficha
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Excluir ficha de avaliação?</AlertDialogTitle><AlertDialogDescription>Ação irreversível. O cadastro básico do praticante permanece.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid="ficha-delete-confirm">Excluir</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button onClick={handleSave} disabled={saving} data-testid="ficha-save-btn" className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white">
                <Save className="h-4 w-4 mr-1" /> {saving ? "Salvando…" : "Salvar ficha"}
              </Button>
              {praticante.tipo === "em_avaliacao" && (
                <Button onClick={() => { setDataInicio(new Date().toISOString().slice(0, 10)); setShowFechar(true); }} className="bg-brand-lilac hover:bg-[#9670B0] text-white" data-testid="ficha-fechar-btn">
                  <ClipboardList className="h-4 w-4 mr-1" /> Fechar avaliação e agendar
                </Button>
              )}
            </div>
          }
        />
      </div>

      {/* Dialog: fechar avaliação + agendar */}
      <Dialog open={showFechar} onOpenChange={setShowFechar}>
        <DialogContent className="max-w-md" data-testid="ficha-fechar-dialog">
          <DialogHeader>
            <DialogTitle>Fechar avaliação e agendar sessões</DialogTitle>
            <DialogDescription>
              O praticante passa para <b>Ativo</b> e as sessões são criadas automaticamente na agenda usando os dias/horário preenchidos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-[#F0FBFC] border border-brand-turquoise/30 p-3 text-xs space-y-1">
              <p><b>Dias:</b> {(form.dias_disponiveis || []).length ? form.dias_disponiveis.join(", ").toUpperCase() : <span className="text-destructive">Não preenchido</span>}</p>
              <p><b>Horário:</b> {form.horario_preferido || <span className="text-destructive">Não preenchido</span>}</p>
              <p><b>Terapeuta:</b> {terapeutas.find((t) => t.id === form.terapeuta_id)?.nome || <span className="text-destructive">Não selecionado</span>}</p>
              <p><b>Cavalo:</b> {cavalos.find((c) => c.id === form.cavalo_id)?.nome || <span className="text-destructive">Não selecionado</span>}</p>
            </div>
            <F label="Data de início das sessões" req>
              <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} data-testid="ficha-data-inicio" />
            </F>
            <F label="Gerar por quantos meses">
              <Select value={String(meses)} onValueChange={(v) => setMeses(parseInt(v, 10))}>
                <SelectTrigger data-testid="ficha-meses"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 mês</SelectItem>
                  <SelectItem value="3">3 meses</SelectItem>
                  <SelectItem value="6">6 meses</SelectItem>
                  <SelectItem value="12">12 meses</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFechar(false)}>Cancelar</Button>
            <Button onClick={handleFecharAvaliacao} disabled={fechando} className="bg-brand-lilac hover:bg-[#9670B0] text-white" data-testid="ficha-fechar-confirm">
              {fechando ? "Agendando…" : "Confirmar e agendar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dados clínicos do praticante — vinculados ao cadastro */}
      <Section title="Dados clínicos do praticante">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <F label="Altura (cm)">
            <Input type="number" step="0.5" min="0" placeholder="Ex: 120" value={pratClinico.altura_cm} onChange={(e) => setPratClinico({ ...pratClinico, altura_cm: e.target.value })} data-testid="ficha-altura" />
          </F>
          <F label="Dominância">
            <Select value={pratClinico.dominancia || ""} onValueChange={(v) => setPratClinico({ ...pratClinico, dominancia: v })}>
              <SelectTrigger data-testid="ficha-dominancia"><SelectValue placeholder="Selecionar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="destro">Destro</SelectItem>
                <SelectItem value="canhoto">Canhoto</SelectItem>
                <SelectItem value="ambidestro">Ambidestro</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Tipo sanguíneo">
            <Select value={pratClinico.tipo_sanguineo || ""} onValueChange={(v) => setPratClinico({ ...pratClinico, tipo_sanguineo: v })}>
              <SelectTrigger data-testid="ficha-tipo-sanguineo"><SelectValue placeholder="Selecionar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="A+">A+</SelectItem>
                <SelectItem value="A-">A−</SelectItem>
                <SelectItem value="B+">B+</SelectItem>
                <SelectItem value="B-">B−</SelectItem>
                <SelectItem value="AB+">AB+</SelectItem>
                <SelectItem value="AB-">AB−</SelectItem>
                <SelectItem value="O+">O+</SelectItem>
                <SelectItem value="O-">O−</SelectItem>
                <SelectItem value="desconhecido">Não sabe informar</SelectItem>
              </SelectContent>
            </Select>
          </F>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <F label="Diagnóstico">
            <Input value={pratClinico.diagnostico} onChange={(e) => setPratClinico({ ...pratClinico, diagnostico: e.target.value })} placeholder="Ex: TEA, Paralisia Cerebral, TDAH…" data-testid="ficha-diagnostico" />
          </F>
          <F label="CID">
            <Input value={pratClinico.cid} onChange={(e) => setPratClinico({ ...pratClinico, cid: e.target.value })} placeholder="Ex: F84.0" data-testid="ficha-cid" />
          </F>
        </div>
      </Section>

      {/* Medicamentos e histórico terapêutico */}
      <Section title="Histórico terapêutico">
        <F label="Quando descobriu o diagnóstico?">
          <Textarea rows={2} value={form.diagnostico_descoberta || ""} onChange={(e) => setForm({ ...form, diagnostico_descoberta: e.target.value })} placeholder="Ex: aos 3 anos, após avaliação neurológica…" data-testid="ficha-diag-descoberta" />
        </F>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Medicamentos em uso">
            <Textarea rows={2} value={form.medicamentos_uso || ""} onChange={(e) => setForm({ ...form, medicamentos_uso: e.target.value })} />
          </F>
          <F label="Já fez equoterapia antes?">
            <SimNao value={form.ja_fez_equoterapia} onChange={(v) => setForm({ ...form, ja_fez_equoterapia: v })} testId="ficha-jafez-eq" />
          </F>
        </div>
        <F label="Outros atendimentos terapêuticos realizados" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {OUTROS_ATENDIMENTOS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border cursor-pointer hover:bg-secondary/40">
                <Checkbox
                  checked={form.outros_atendimentos?.includes(opt.value)}
                  onCheckedChange={() => toggleAtendimento(opt.value)}
                  data-testid={`ficha-atend-${opt.value}`}
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
          <Textarea
            className="mt-2"
            rows={2}
            value={form.outros_atendimentos_desc || ""}
            onChange={(e) => setForm({ ...form, outros_atendimentos_desc: e.target.value })}
            placeholder="Outros atendimentos não listados acima…"
            data-testid="ficha-outros-atend-desc"
          />
        </F>
      </Section>

      {/* Histórico gestacional */}
      <Section title="Histórico gestacional">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Gravidez planejada?"><SimNao value={form.gravidez_planejada} onChange={(v) => setForm({ ...form, gravidez_planejada: v })} /></F>
          <F label="Fez pré-natal?"><SimNao value={form.fez_prenatal} onChange={(v) => setForm({ ...form, fez_prenatal: v })} /></F>
          <F label="Tipo de parto">
            <Select value={form.tipo_parto || ""} onValueChange={(v) => setForm({ ...form, tipo_parto: v })}>
              <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="cesarea">Cesárea</SelectItem>
                <SelectItem value="forceps">Fórceps</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Semanas de gestação"><Input type="number" min="20" max="45" value={form.semanas_gestacao} onChange={(e) => setForm({ ...form, semanas_gestacao: e.target.value })} /></F>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <F label="Período pós-natal"><Textarea rows={2} value={form.pos_natal || ""} onChange={(e) => setForm({ ...form, pos_natal: e.target.value })} /></F>
          <F label="Complicações"><Textarea rows={2} value={form.complicacoes || ""} onChange={(e) => setForm({ ...form, complicacoes: e.target.value })} /></F>
        </div>
      </Section>

      {/* Marcos motores */}
      <Section title="Marcos motores (idade em meses)">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <F label="Sustentação da cabeça"><Input type="number" min="0" value={form.marco_cabeca_meses} onChange={(e) => setForm({ ...form, marco_cabeca_meses: e.target.value })} /></F>
          <F label="Sentar"><Input type="number" min="0" value={form.marco_sentar_meses} onChange={(e) => setForm({ ...form, marco_sentar_meses: e.target.value })} /></F>
          <F label="Engatinhar"><Input type="number" min="0" value={form.marco_engatinhar_meses} onChange={(e) => setForm({ ...form, marco_engatinhar_meses: e.target.value })} /></F>
          <F label="Andar"><Input type="number" min="0" value={form.marco_andar_meses} onChange={(e) => setForm({ ...form, marco_andar_meses: e.target.value })} /></F>
        </div>
      </Section>

      {/* Desenvolvimento motor */}
      <Section title="Desenvolvimento motor">
        <F label="Tônus muscular (escala -1 a 4)">
          <ScaleTonus value={form.tonus_muscular} onChange={(v) => setForm({ ...form, tonus_muscular: v })} />
        </F>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <F label="Usa órteses / próteses?"><SimNao value={form.usa_orteses} onChange={(v) => setForm({ ...form, usa_orteses: v })} /></F>
          {form.usa_orteses && (
            <F label="Descrição das órteses">
              <Input value={form.orteses_desc || ""} onChange={(e) => setForm({ ...form, orteses_desc: e.target.value })} placeholder="Ex: órtese suropodálica bilateral" />
            </F>
          )}
        </div>

        <div className="mt-6">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1">Mudança de decúbito</p>
          <p className="text-[11px] text-muted-foreground mb-3">
            Legenda: <b>I</b> = Independente · <b>D</b> = Dependente (com auxílio) · <b>NR</b> = Não realiza
          </p>
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-500 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Mudança</th>
                  <th className="text-center px-3 py-2 w-16">I</th>
                  <th className="text-center px-3 py-2 w-16">D</th>
                  <th className="text-center px-3 py-2 w-16">NR</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "sentado_pe", label: "Sentado para em pé" },
                  { key: "pe_sentado", label: "Em pé para sentado" },
                  { key: "sentado_deitado", label: "Sentado para deitado" },
                  { key: "dorsal_ventral", label: "Decúbito dorsal para ventral" },
                  { key: "ventral_dorsal", label: "Decúbito ventral para dorsal" },
                ].map((row) => {
                  const cur = form.mudancas_decubito?.[row.key] || "";
                  return (
                    <tr key={row.key} className="border-t border-border" data-testid={`ficha-decubito-${row.key}`}>
                      <td className="px-3 py-2 text-slate-700">{row.label}</td>
                      {["I", "D", "NR"].map((opt) => (
                        <td key={opt} className="text-center py-2 px-1">
                          <button
                            type="button"
                            onClick={() => setForm({
                              ...form,
                              mudancas_decubito: { ...(form.mudancas_decubito || {}), [row.key]: cur === opt ? "" : opt },
                            })}
                            className={`h-8 w-10 rounded-md border text-xs font-bold transition-colors ${cur === opt ? "bg-brand-turquoise text-white border-brand-turquoise" : "bg-white border-border hover:bg-secondary/40 text-slate-600"}`}
                            data-testid={`ficha-decubito-${row.key}-${opt}`}
                          >
                            {opt}
                          </button>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <F label="Desenvolvimento da linguagem" className="mt-6">
          <Select value={form.linguagem_desenvolvimento || ""} onValueChange={(v) => setForm({ ...form, linguagem_desenvolvimento: v })}>
            <SelectTrigger data-testid="ficha-linguagem"><SelectValue placeholder="Selecionar" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="dentro_esperado">Dentro do esperado</SelectItem>
              <SelectItem value="atraso_leve">Atraso leve</SelectItem>
              <SelectItem value="atraso_moderado">Atraso moderado</SelectItem>
              <SelectItem value="atraso_severo">Atraso severo</SelectItem>
              <SelectItem value="nao_verbal">Não verbal</SelectItem>
            </SelectContent>
          </Select>
        </F>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <F label="Quando falou a primeira palavra?">
            <Input value={form.primeira_palavra_idade || ""} onChange={(e) => setForm({ ...form, primeira_palavra_idade: e.target.value })} placeholder="Ex: aos 12 meses" data-testid="ficha-primeira-palavra" />
          </F>
          <F label="Constrói frases?"><SimNao value={form.constroi_frases} onChange={(v) => setForm({ ...form, constroi_frases: v })} testId="ficha-constroi-frases" /></F>
        </div>
      </Section>

      {/* Perfil comportamental (escala 0-4) */}
      <Section title="Desenvolvimento psicológico e comportamental (0 = ausente · 4 = intenso)">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Scale05 label="Ansiedade" value={form.ansiedade} onChange={(v) => setForm({ ...form, ansiedade: v })} />
          <Scale05 label="Autoestima" value={form.autoestima} onChange={(v) => setForm({ ...form, autoestima: v })} />
          <Scale05 label="Autoconfiança" value={form.autoconfianca} onChange={(v) => setForm({ ...form, autoconfianca: v })} />
          <Scale05 label="Introversão" value={form.introversao} onChange={(v) => setForm({ ...form, introversao: v })} />
        </div>
      </Section>

      {/* Interação e rotina */}
      <Section title="Interação social e rotina">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Interação social">
            <Select value={form.interacao_social || ""} onValueChange={(v) => setForm({ ...form, interacao_social: v })}>
              <SelectTrigger data-testid="ficha-int-social"><SelectValue placeholder="Selecionar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="adequada">Adequada</SelectItem>
                <SelectItem value="limitada">Limitada</SelectItem>
                <SelectItem value="com_dificuldade">Com dificuldade</SelectItem>
                <SelectItem value="ausente">Ausente</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Interação familiar">
            <Select value={form.interacao_familiar || ""} onValueChange={(v) => setForm({ ...form, interacao_familiar: v })}>
              <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="harmoniosa">Harmoniosa</SelectItem>
                <SelectItem value="conflituosa">Conflituosa</SelectItem>
                <SelectItem value="dependente">Dependente</SelectItem>
                <SelectItem value="distante">Distante</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Hábitos de sono">
            <Select value={form.habitos_sono || ""} onValueChange={(v) => setForm({ ...form, habitos_sono: v })}>
              <SelectTrigger data-testid="ficha-sono"><SelectValue placeholder="Selecionar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="regular">Regular</SelectItem>
                <SelectItem value="irregular">Irregular</SelectItem>
                <SelectItem value="insonia">Insônia</SelectItem>
                <SelectItem value="sono_excessivo">Sono excessivo</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Alimentação">
            <Select value={form.alimentacao || ""} onValueChange={(v) => setForm({ ...form, alimentacao: v })}>
              <SelectTrigger data-testid="ficha-alimentacao"><SelectValue placeholder="Selecionar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="equilibrada">Equilibrada</SelectItem>
                <SelectItem value="seletiva">Seletiva</SelectItem>
                <SelectItem value="restritiva">Restritiva</SelectItem>
                <SelectItem value="sonda">Via sonda</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Cardápio típico do dia" className="md:col-span-2">
            <Textarea rows={3} value={form.cardapio || ""} onChange={(e) => setForm({ ...form, cardapio: e.target.value })} placeholder="Café da manhã / Almoço / Lanche / Jantar…" data-testid="ficha-cardapio" />
          </F>
          <F label="Dorme sozinho?"><SimNao value={form.dorme_sozinho} onChange={(v) => setForm({ ...form, dorme_sozinho: v })} testId="ficha-dorme-sozinho" /></F>
          <F label="O sono é agitado?"><SimNao value={form.sono_agitado} onChange={(v) => setForm({ ...form, sono_agitado: v })} testId="ficha-sono-agitado" /></F>
          <F label="Personalidade">
            <Select value={form.personalidade || ""} onValueChange={(v) => setForm({ ...form, personalidade: v })}>
              <SelectTrigger data-testid="ficha-personalidade"><SelectValue placeholder="Selecionar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="extrovertido">Extrovertido</SelectItem>
                <SelectItem value="introvertido">Introvertido</SelectItem>
                <SelectItem value="timido">Tímido</SelectItem>
                <SelectItem value="misto">Misto</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Tem amigos?"><SimNao value={form.tem_amigos} onChange={(v) => setForm({ ...form, tem_amigos: v })} testId="ficha-tem-amigos" /></F>
          <F label="Faz amizade com facilidade?"><SimNao value={form.amizade_facil} onChange={(v) => setForm({ ...form, amizade_facil: v })} testId="ficha-amizade-facil" /></F>
          <F label="Atividades de vida diária (AVDs)" className="md:col-span-2">
            <Textarea rows={2} value={form.atividades_vida_diaria || ""} onChange={(e) => setForm({ ...form, atividades_vida_diaria: e.target.value })} placeholder="Descreva independência em alimentação, higiene, vestir-se, banho, controle esfincteriano…" />
          </F>
        </div>
      </Section>

      {/* Ambiente familiar */}
      <Section title="Ambiente familiar e social">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Tem irmãos?" className="md:col-span-1"><SimNao value={form.tem_irmaos} onChange={(v) => setForm({ ...form, tem_irmaos: v })} testId="ficha-tem-irmaos" /></F>
          <F label="Quantos e quais? (nome / idade)" className="md:col-span-1">
            <Textarea rows={2} value={form.irmaos_descricao || ""} onChange={(e) => setForm({ ...form, irmaos_descricao: e.target.value })} placeholder="Ex: Pedro (8) e Ana (5)" data-testid="ficha-irmaos-desc" />
          </F>
          <F label="Os pais são casados e moram juntos?"><SimNao value={form.pais_casados} onChange={(v) => setForm({ ...form, pais_casados: v })} testId="ficha-pais-casados" /></F>
          <F label="Quem mais acompanha o paciente?" className="md:col-span-2">
            <Textarea rows={2} value={form.quem_acompanha_paciente || ""} onChange={(e) => setForm({ ...form, quem_acompanha_paciente: e.target.value })} placeholder="Ex: avó materna, tia, cuidador, professor…" />
          </F>
          <F label="Familiares com deficiência ou doenças genéticas?" className="md:col-span-2">
            <Textarea rows={2} value={form.familiares_com_deficiencia_desc || ""} onChange={(e) => setForm({ ...form, familiares_com_deficiencia_desc: e.target.value })} placeholder="Descreva (parentesco, deficiência/doença)…" data-testid="ficha-familiares-desc" />
          </F>
          <F label="Pessoas do convívio fazem uso de drogas / cigarro / álcool?" className="md:col-span-2">
            <Textarea rows={2} value={form.convive_com_drogas_alcool_desc || ""} onChange={(e) => setForm({ ...form, convive_com_drogas_alcool_desc: e.target.value })} placeholder="Descreva…" data-testid="ficha-drogas-desc" />
          </F>
        </div>
      </Section>

      {/* Rotina e comportamento */}
      <Section title="Rotina e comportamento">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Amamentação — seio / mamadeira / chupeta (até quando?)" className="md:col-span-2">
            <Textarea rows={2} value={form.amamentacao_desmame || ""} onChange={(e) => setForm({ ...form, amamentacao_desmame: e.target.value })} placeholder="Ex: mamou no seio até 1 ano; usou chupeta até 3 anos…" data-testid="ficha-amamentacao" />
          </F>
          <F label="Range os dentes?"><SimNao value={form.range_dentes} onChange={(v) => setForm({ ...form, range_dentes: v })} testId="ficha-range-dentes" /></F>
          <F label="Fala durante a noite?"><SimNao value={form.fala_dormindo} onChange={(v) => setForm({ ...form, fala_dormindo: v })} testId="ficha-fala-dormindo" /></F>
          <F label="Comporta-se de acordo com a faixa etária?"><SimNao value={form.comporta_faixa_etaria} onChange={(v) => setForm({ ...form, comporta_faixa_etaria: v })} testId="ficha-comporta-idade" /></F>
          <F label="É agressivo?"><SimNao value={form.e_agressivo} onChange={(v) => setForm({ ...form, e_agressivo: v })} testId="ficha-agressivo" /></F>
          <F label="É teimoso?"><SimNao value={form.e_teimoso} onChange={(v) => setForm({ ...form, e_teimoso: v })} testId="ficha-teimoso" /></F>
          <F label="É carinhoso?"><SimNao value={form.e_carinhoso} onChange={(v) => setForm({ ...form, e_carinhoso: v })} testId="ficha-carinhoso" /></F>
          <F label="Tem medo de algo?"><Textarea rows={2} value={form.tem_medos || ""} onChange={(e) => setForm({ ...form, tem_medos: e.target.value })} placeholder="Descreva medos (barulhos, altura, animais…)" /></F>
          <F label="Tem manias ou tiques?"><Textarea rows={2} value={form.tem_manias_tiques || ""} onChange={(e) => setForm({ ...form, tem_manias_tiques: e.target.value })} placeholder="Descreva manias ou tiques observados" /></F>
        </div>
      </Section>

      {/* Interesses e convívio */}
      <Section title="Interesses e convívio">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Assiste TV? Ouve música? (o quê?)"><Textarea rows={2} value={form.assiste_tv_musica || ""} onChange={(e) => setForm({ ...form, assiste_tv_musica: e.target.value })} placeholder="Ex: assiste desenhos; gosta de música clássica…" /></F>
          <F label="Passeia? Onde?"><Textarea rows={2} value={form.passeios_onde || ""} onChange={(e) => setForm({ ...form, passeios_onde: e.target.value })} placeholder="Ex: shopping, parque, casa de familiares…" /></F>
          <F label="Estuda?"><SimNao value={form.estuda} onChange={(v) => setForm({ ...form, estuda: v })} testId="ficha-estuda" /></F>
          <F label="Nome da escola"><Input value={form.escola_nome || ""} onChange={(e) => setForm({ ...form, escola_nome: e.target.value })} data-testid="ficha-escola-nome" /></F>
          <F label="Comportamento escolar" className="md:col-span-2"><Textarea rows={2} value={form.comportamento_escolar || ""} onChange={(e) => setForm({ ...form, comportamento_escolar: e.target.value })} placeholder="Ex: interação com colegas, aprendizado, dificuldades…" /></F>
          <F label="Tem interesse sexual?" className="md:col-span-2"><SimNao value={form.tem_interesse_sexual} onChange={(v) => setForm({ ...form, tem_interesse_sexual: v })} testId="ficha-interesse-sexual" /></F>
        </div>
      </Section>

      {/* Objetivos */}
      <Section title="Objetivos da terapia">
        <div className="grid grid-cols-1 gap-4">
          <F label="Objetivo geral" data-testid="ficha-objetivo-geral">
            <Textarea
              rows={3}
              value={form.objetivo_geral || ""}
              onChange={(e) => setForm({ ...form, objetivo_geral: e.target.value })}
              placeholder="Ex: Melhorar equilíbrio, coordenação motora e interação social por meio da equoterapia…"
              data-testid="ficha-objetivo-geral-input"
            />
          </F>
          <F label="Queixa principal"><Textarea rows={2} value={form.queixa_principal || ""} onChange={(e) => setForm({ ...form, queixa_principal: e.target.value })} data-testid="ficha-queixa" /></F>
          <F label="Expectativas da família"><Textarea rows={2} value={form.expectativas_familia || ""} onChange={(e) => setForm({ ...form, expectativas_familia: e.target.value })} /></F>
          <F label="Objetivos específicos da terapia"><Textarea rows={2} value={form.objetivos_terapia || ""} onChange={(e) => setForm({ ...form, objetivos_terapia: e.target.value })} placeholder="Ex: Manter o tronco ereto por 5 minutos; realizar transição de decúbito com auxílio; ampliar vocabulário…" /></F>
          <F label="Por onde nos conheceu?"><Textarea rows={2} value={form.como_conheceu || ""} onChange={(e) => setForm({ ...form, como_conheceu: e.target.value })} placeholder="Ex: indicação médica, Instagram, amigo…" data-testid="ficha-como-conheceu" /></F>
        </div>
      </Section>

      {/* Recursos definidos na avaliação */}
      <Section title="Recursos da equoterapia">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <F label="Cavalo indicado">
            <Select value={form.cavalo_id || ""} onValueChange={(v) => setForm({ ...form, cavalo_id: v })}>
              <SelectTrigger data-testid="ficha-cavalo"><SelectValue placeholder="Selecionar cavalo" /></SelectTrigger>
              <SelectContent>
                {cavalos.length === 0 ? (
                  <SelectItem value="__none__" disabled>Nenhum cavalo cadastrado</SelectItem>
                ) : cavalos.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.nome}{c.pelagem ? ` — ${c.pelagem}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F label="Terapeuta responsável">
            <Select value={form.terapeuta_id || ""} onValueChange={(v) => setForm({ ...form, terapeuta_id: v })}>
              <SelectTrigger data-testid="ficha-terapeuta"><SelectValue placeholder="Selecionar terapeuta" /></SelectTrigger>
              <SelectContent>
                {terapeutas.length === 0 ? (
                  <SelectItem value="__none__" disabled>Nenhum terapeuta cadastrado</SelectItem>
                ) : terapeutas.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.nome}{t.especialidade ? ` — ${especialidadeLabel(t.especialidade)}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F label="Material a utilizar">
            <Select value={form.material_utilizado || ""} onValueChange={(v) => setForm({ ...form, material_utilizado: v })}>
              <SelectTrigger data-testid="ficha-material"><SelectValue placeholder="Selecionar material" /></SelectTrigger>
              <SelectContent>
                {MATERIAIS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F label="Quem irá acompanhá-lo na sessão?" className="md:col-span-3">
            <Textarea
              rows={2}
              value={form.acompanhante_avaliacao || ""}
              onChange={(e) => setForm({ ...form, acompanhante_avaliacao: e.target.value })}
              placeholder="Ex: mãe, pai, avó materna, cuidador…"
              data-testid="ficha-acompanhante"
            />
          </F>
        </div>
      </Section>

      {/* Horário desejado */}
      <Section title="Horário desejado para as sessões">
        <div className="grid grid-cols-1 gap-4">
          <F label="Dias da semana disponíveis">
            <div className="grid grid-cols-3 sm:grid-cols-7 gap-2" data-testid="ficha-dias-disponiveis">
              {[
                { v: "seg", label: "Seg" },
                { v: "ter", label: "Ter" },
                { v: "qua", label: "Qua" },
                { v: "qui", label: "Qui" },
                { v: "sex", label: "Sex" },
                { v: "sab", label: "Sáb" },
                { v: "dom", label: "Dom" },
              ].map((d) => {
                const sel = (form.dias_disponiveis || []).includes(d.v);
                return (
                  <button
                    key={d.v} type="button"
                    onClick={() => {
                      const cur = form.dias_disponiveis || [];
                      setForm({
                        ...form,
                        dias_disponiveis: sel ? cur.filter((x) => x !== d.v) : [...cur, d.v],
                      });
                    }}
                    className={`py-2 px-2 rounded-lg border font-semibold text-sm transition-colors ${sel ? "bg-brand-turquoise text-white border-brand-turquoise" : "bg-white border-border hover:bg-secondary/40 text-slate-700"}`}
                    data-testid={`ficha-dia-${d.v}`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </F>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <F label="Horário preferido">
              <Input
                type="time"
                value={form.horario_preferido || ""}
                onChange={(e) => setForm({ ...form, horario_preferido: e.target.value })}
                data-testid="ficha-horario-preferido"
              />
            </F>
            <F label="Observações sobre disponibilidade" className="md:col-span-2">
              <Input
                value={form.disponibilidade_obs || ""}
                onChange={(e) => setForm({ ...form, disponibilidade_obs: e.target.value })}
                placeholder="Ex: só depois das 15h; alterna entre terça e quinta…"
                data-testid="ficha-disponibilidade-obs"
              />
            </F>
          </div>
        </div>
      </Section>

      <div className="flex justify-end pt-4">
        <Button onClick={handleSave} disabled={saving} className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white" data-testid="ficha-save-btn-bottom">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Salvando…" : "Salvar ficha"}
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Card className="border-border shadow-none rounded-xl bg-white">
      <CardContent className="p-6">
        <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-brand-turquoise mb-4">
          {title}
        </h3>
        {children}
      </CardContent>
    </Card>
  );
}

function F({ label, req, className, children }) {
  return (
    <div className={`space-y-1.5 ${className || ""}`}>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}{req && <span className="text-destructive"> *</span>}
      </Label>
      {children}
    </div>
  );
}

function SimNao({ value, onChange, testId }) {
  return (
    <div className="flex gap-2" data-testid={testId}>
      <Button type="button" size="sm" variant={value === true ? "default" : "outline"} className={value === true ? "bg-brand-turquoise hover:bg-[#3A9CA2]" : ""} onClick={() => onChange(true)}>Sim</Button>
      <Button type="button" size="sm" variant={value === false ? "default" : "outline"} className={value === false ? "bg-brand-turquoise hover:bg-[#3A9CA2]" : ""} onClick={() => onChange(false)}>Não</Button>
      {value !== null && value !== undefined && (
        <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null)} className="text-muted-foreground">Limpar</Button>
      )}
    </div>
  );
}

function ScaleTonus({ value, onChange }) {
  const opts = [
    { v: -1, label: "Hipotonia" },
    { v: 0, label: "Tônus normal" },
    { v: 1, label: "Discreto aumento do tônus" },
    { v: 2, label: "Aumento mais pronunciado do tônus" },
    { v: 3, label: "Aumento considerável do tônus" },
    { v: 4, label: "Articulação afetada rígida em flexão ou extensão" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="ficha-tonus">
      {opts.map((o) => (
        <button
          key={o.v} type="button"
          onClick={() => onChange(o.v)}
          className={`h-auto py-3 px-2 rounded-lg border transition-colors text-center flex flex-col items-center gap-1 min-h-[92px] ${value === o.v ? "bg-brand-turquoise text-white border-brand-turquoise" : "bg-white border-border hover:bg-secondary/40"}`}
        >
          <div className="text-2xl font-bold leading-none">{o.v}</div>
          <div className={`text-[10px] leading-tight ${value === o.v ? "text-white/90" : "text-muted-foreground"}`}>{o.label}</div>
        </button>
      ))}
    </div>
  );
}

function Scale05({ label, value, onChange }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="flex gap-2">
        {[0, 1, 2, 3, 4].map((v) => (
          <button
            key={v} type="button"
            onClick={() => onChange(v)}
            className={`flex-1 h-11 rounded-lg border text-sm font-semibold transition-colors ${value === v ? "bg-brand-lilac text-white border-brand-lilac" : "bg-white border-border hover:bg-secondary/40"}`}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}
